import React, { FC, useState } from 'react';

import IconCheck from '@/assets/check.svg?react';
import IconChevron from '@/assets/chevron.svg?react';
import IconCross from '@/assets/cross.svg?react';
import IconQuestion from '@/assets/question-simple.svg?react';
import Collapse from '@/components/ui/Collapse';
import cn from '@/utils/cn';

import { useLingui } from '@lingui/react';
import FsLightbox from 'fslightbox-react';

export interface InstructionProps
  extends React.ComponentPropsWithoutRef<'div'> {
  title: string;
  description?: string;
  steps?: React.ReactNode[];
  correctImg?: string;
  wrongImg?: string;
}

const Instruction: FC<InstructionProps> = ({
  title,
  description,
  steps,
  correctImg,
  wrongImg,
  className,
  ...props
}) => {
  return (
    <Collapse defaultOpen={true} title={<Title title={title} />}>
      <div
        className={cn(
          'special-body bg-gray-200 px-3 pb-2.5 pt-0.5 sm:pb-3 md:pt-0',
          className
        )}
        {...props}
      >
        {description && description}
        <div data-testid="steps" className="flex flex-col gap-3 sm:flex-row">
          {correctImg && wrongImg && (
            <div className="flex gap-1.5 sm:gap-1">
              <ImageWithIcon
                variant="correct"
                correctImgSrc={correctImg}
                wrongImgSrc={wrongImg}
              />
              <ImageWithIcon
                variant="wrong"
                correctImgSrc={correctImg}
                wrongImgSrc={wrongImg}
              />
            </div>
          )}
          {steps && <List data={steps} />}
        </div>
      </div>
    </Collapse>
  );
};

export default Instruction;

const Title = ({ title }: { title: string }) => (
  <div className="caption-large-semi group flex w-full items-center gap-2 bg-gray-200 px-3 py-2.5 text-dark-gray-500 sm:p-3">
    <div className="flex aspect-square w-[18px] shrink-0 items-center justify-center rounded-full bg-primary">
      <IconQuestion width={10} className="text-white" />
    </div>
    <span className="w-full text-start lg:font-bold">{title}</span>
    <div>
      <IconChevron
        className="text-dark-gray-500 transition-transform duration-400 group-data-[state=closed]:rotate-90 group-data-[state=open]:rotate-[270deg]"
        width={16}
      />
    </div>
  </div>
);

const List = ({ data }: { data: React.ReactNode[] }) => (
  <ol className="my-auto list-decimal pl-5">
    {data.map((item, index) => (
      <li className="special-body text-dark-gray-500" key={index}>
        {item}
      </li>
    ))}
  </ol>
);

const ImageWithIcon = ({
  variant,
  correctImgSrc,
  wrongImgSrc,
}: {
  variant: 'correct' | 'wrong';
  correctImgSrc: string;
  wrongImgSrc: string;
}) => {
  const { i18n } = useLingui();
  const [toggler, setToggler] = useState<null | 'correct' | 'wrong'>(null);

  return (
    <div className="flex flex-1 gap-1.5 rounded-md bg-gray-100 p-2 sm:h-[106px] lg:min-w-max lg:flex-none">
      <span
        className={`flex items-center rounded-sm p-0.5 ${
          variant === 'correct' ? 'bg-green-500' : 'bg-red-500'
        }`}
      >
        {variant === 'correct' ? (
          <IconCheck width={12} className="text-gray-100" />
        ) : (
          <IconCross width={12} className="text-gray-100" />
        )}
      </span>
      <div>
        <img
          onClick={() =>
            setToggler((prev) => (prev === variant ? null : variant))
          }
          className="aspect-[13/18] h-auto w-full cursor-pointer rounded-sm object-contain brightness-95 transition-all duration-200 hover:brightness-[85%] sm:h-[90px] sm:w-auto sm:min-w-[65px]"
          src={variant === 'correct' ? correctImgSrc : wrongImgSrc}
          alt={
            variant === 'correct'
              ? i18n.t('Poprawne zdjęcie')
              : i18n.t('Niepoprawne zdjęcie')
          }
        />
        <FsLightbox
          toggler={!!toggler}
          sourceIndex={variant === 'correct' ? 0 : 1}
          sources={[correctImgSrc, wrongImgSrc]}
        />
      </div>
    </div>
  );
};
